import apiClient, { unwrapData } from "./apiClient";
import {
  clearAuthSession,
  getStoredAuthSession,
  getStoredToken,
  getStoredUser,
  hasStoredSession,
  isStoredSessionPersistent,
  persistAuthSession,
} from "./authStorage";

function humanizeRole(role) {
  const normalizedRole = String(role || "").trim();

  if (!normalizedRole) {
    return "";
  }

  return normalizedRole
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function normalizeRoleKey(role) {
  return String(role || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function buildAvatarInitials(name, email) {
  const source = String(name || email || "").trim();

  if (!source) {
    return "AU";
  }

  const words = source
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);

  if (!words.length) {
    return "AU";
  }

  return words.map((word) => word[0].toUpperCase()).join("");
}

function deriveRoleLabel(user) {
  const firstRole = user?.role || (Array.isArray(user?.roles) ? user.roles[0] : "");

  if (firstRole) {
    return humanizeRole(firstRole);
  }

  if (/^admin@/i.test(String(user?.email || ""))) {
    return "Admin";
  }

  return "Authenticated User";
}

function extractLoginErrorMessage(error) {
  const fallbackMessage = "Invalid email or password";
  const details = error?.details;

  if (typeof details === "string" && details.trim()) {
    return details.trim();
  }

  const detailMessage = String(
    details?.message || details?.error || error?.rawMessage || ""
  ).trim();

  if (detailMessage) {
    return detailMessage;
  }

  if (Array.isArray(details?.errors) && details.errors.length) {
    const firstError = details.errors.find((item) =>
      String(item?.message || item || "").trim()
    );

    if (firstError) {
      return String(firstError.message || firstError).trim();
    }
  }

  if (typeof error?.message === "string" && error.message.trim()) {
    const normalized = error.message.trim();

    if (
      normalized === "Invalid login credentials." ||
      normalized === "Your session has expired. Please login again."
    ) {
      return fallbackMessage;
    }

    return normalized;
  }

  return fallbackMessage;
}

export function normalizeAuthUser(user = {}) {
  const profileName = String(user?.profile?.full_name || "").trim();
  const name = profileName || user?.username || user?.email || "User";
  const roleKey = normalizeRoleKey(user?.role || user?.roles?.[0] || "");

  return {
    id: String(user?.user_id || user?.id || ""),
    userId: user?.user_id || user?.id || null,
    name,
    role: deriveRoleLabel(user),
    roleKey,
    roleId: user?.role_id || null,
    agentId: user?.agent_id || null,
    email: user?.email || "",
    avatarInitials: buildAvatarInitials(name, user?.email),
    roles: Array.isArray(user?.roles) ? user.roles : [],
    permissions: Array.isArray(user?.permissions) ? user.permissions : [],
    profile: user?.profile || {},
    createdAt: user?.created_at || null,
    raw: user,
  };
}

export function getCurrentUser() {
  return getStoredUser();
}

export function isAuthenticated() {
  return hasStoredSession();
}

export async function loginUser(credentials = {}) {
  const identifier = String(credentials.identifier || "").trim();
  const password = String(credentials.password || "");
  const rememberMe = Boolean(credentials.rememberMe);

  try {
    const response = await apiClient.post("/api/auth/login", {
      skipAuth: true,
      body: {
        emailOrUsername: identifier,
        password,
      },
    });
    const payload = unwrapData(response) || {};
    const user = normalizeAuthUser(payload.user || {});

    persistAuthSession(
      {
        token: payload.accessToken,
        tokenType: payload.tokenType,
        expiresIn: payload.expiresIn,
        user,
      },
      rememberMe,
    );

    return user;
  } catch (error) {
    throw new Error(extractLoginErrorMessage(error));
  }
}

export async function fetchAuthenticatedUser(options = {}) {
  const response = await apiClient.get("/api/auth/me", options);
  const user = normalizeAuthUser(unwrapData(response) || {});
  const currentSession = getStoredAuthSession();

  persistAuthSession(
    {
      token: getStoredToken(),
      user,
      tokenType: currentSession?.tokenType || "Bearer",
      expiresIn: currentSession?.expiresIn || null,
    },
    isStoredSessionPersistent(),
  );

  return user;
}

export async function logoutUser() {
  try {
    if (hasStoredSession()) {
      await apiClient.post("/api/auth/logout", {
        body: {},
      });
    }
  } catch (error) {
    // Best effort logout so stale server-side session failures do not block UI logout.
  } finally {
    clearAuthSession();
  }
}
